// FormPreviewDialog — read-only popup preview of a form template (how it looks when filled in)
import { X, Camera, PenLine, Info, FileText } from "lucide-react";
import Pill from "@/components/Pill";

const STATUS_TONE = { draft: "amber", published: "green", archived: "neutral" };

// Shared read-only render of a template's sections/fields — used by the
// list-level modal below AND the live side-by-side preview in FormBuilder.
export function FormPreviewBody({ template, testIdPrefix = "form-preview" }) {
    const sections = [...(template?.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const fieldCount = sections.reduce((n, s) => n + (s.fields || []).length, 0);
    return (
        <div className="space-y-6">
            {template?.description && (
                <p className="text-[13px] text-[#6F6A64] bg-[#FAF9F7] border border-[#EFEBE5] rounded-lg p-3">
                    {template.description}
                </p>
            )}

            {sections.length === 0 || fieldCount === 0 ? (
                <div className="text-center py-12 text-[#9A968F]" data-testid={`${testIdPrefix}-empty`}>
                    <FileText size={28} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">This template has no fields yet.</p>
                    <p className="text-[12px] mt-1">Add sections and questions on the left to see them appear here.</p>
                </div>
            ) : sections.map((sec) => (
                <div key={sec.id} data-testid={`${testIdPrefix}-section-${sec.id}`}>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide text-[#1F1F1F] pb-2 mb-3 border-b border-[#E5E0DA] flex items-center gap-2">
                        {sec.title}
                        {sec.show_if && <span className="text-[9.5px] font-semibold normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-[#FBEFE5] text-[#9F4212]" title="Shown only when a condition is met">Conditional</span>}
                    </h4>
                    <div className="space-y-4">
                        {(sec.fields || []).map((f) => <PreviewField key={f.id} field={f} testIdPrefix={testIdPrefix} />)}
                        {(sec.fields || []).length === 0 && (
                            <p className="text-[12px] text-[#9A968F] italic">No fields in this section.</p>
                        )}
                    </div>
                </div>
            ))}

            {template?.requires_signature && fieldCount > 0 && (
                <div data-testid={`${testIdPrefix}-signature-required`}>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide text-[#1F1F1F] pb-2 mb-3 border-b border-[#E5E0DA]">
                        Sign-off
                    </h4>
                    <FieldLabel label="Signature" required />
                    <SignatureBox />
                </div>
            )}
        </div>
    );
}

export default function FormPreviewDialog({ template, onClose }) {
    if (!template) return null;
    const sections = template.sections || [];
    const fieldCount = sections.reduce((n, s) => n + (s.fields || []).length, 0);

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="form-preview-dialog"
            onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-start justify-between bg-[#F5F3F0]">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10.5px] font-bold tracking-[.08em] uppercase text-[#9A968F]">Preview</span>
                            <Pill tone={STATUS_TONE[template.status] || "neutral"}>{template.status}</Pill>
                        </div>
                        <h3 className="text-lg font-bold text-[#1F1F1F] mt-1 truncate">{template.name}</h3>
                        <p className="text-[12px] text-[#6F6A64] mt-0.5">
                            <span className="font-mono">{template.code}</span> · {template.category} · v{template.version || 1}
                            {" · "}{sections.length} section{sections.length === 1 ? "" : "s"} · {fieldCount} field{fieldCount === 1 ? "" : "s"}
                        </p>
                    </div>
                    <button data-testid="form-preview-close" onClick={onClose}
                        className="p-1 text-[#9A968F] hover:text-[#1F1F1F] shrink-0"><X size={18} /></button>
                </div>

                {/* Body */}
                <div className="p-5 overflow-y-auto">
                    <FormPreviewBody template={template} />
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-between bg-[#F5F3F0]">
                    <span className="text-[11px] text-[#9A968F]">Read-only preview — fields are not interactive.</span>
                    <button data-testid="form-preview-done" onClick={onClose}
                        className="px-3.5 py-2 text-sm font-semibold rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>Done</button>
                </div>
            </div>
        </div>
    );
}

function FieldLabel({ label, required, help }) {
    return (
        <div className="mb-1.5">
            <label className="text-[13px] font-semibold text-[#1F1F1F]">
                {label || <span className="italic text-[#9A968F]">Untitled question</span>}
                {required && <span className="text-[#C0392B] ml-0.5">*</span>}
            </label>
            {help && <p className="text-[11.5px] text-[#9A968F] mt-0.5">{help}</p>}
        </div>
    );
}

const fakeInput = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-[#FAF9F7] text-sm text-[#9A968F] flex items-center cursor-default select-none";

function PreviewField({ field, testIdPrefix = "form-preview" }) {
    const type = field.field_type;

    if (type === "instruction") {
        return (
            <div className="flex gap-2 items-start bg-[#EEF3FB] border border-[#D6E2F5] rounded-lg p-3" data-testid={`${testIdPrefix}-field-${field.id}`}>
                <Info size={15} className="text-[#2A5DB0] mt-0.5 shrink-0" />
                <div>
                    <p className="text-[13px] text-[#33415C] whitespace-pre-wrap">{field.content || field.label || "Instruction text"}</p>
                    {field.internal_only && <p className="text-[10.5px] font-semibold text-[#9F4212] mt-1">Internal only — never shown to the client</p>}
                </div>
            </div>
        );
    }

    return (
        <div data-testid={`${testIdPrefix}-field-${field.id}`}>
            <FieldLabel label={field.label} required={field.required} help={field.help_text} />
            {field.show_if && <p className="text-[10.5px] text-[#9F4212] font-semibold mb-1">Conditional — shown only when a condition is met</p>}
            <FieldControl field={field} />
        </div>
    );
}

function FieldControl({ field }) {
    const { field_type: type, placeholder, options = [] } = field;

    switch (type) {
        case "textarea":
            return <div className={fakeInput + " h-16 items-start pt-2"}>{placeholder || "Long answer text…"}</div>;
        case "number":
            return <div className={fakeInput}>{placeholder || "0"}</div>;
        case "date":
            return <div className={fakeInput}>dd / mm / yyyy</div>;
        case "dropdown":
            return (
                <div className={fakeInput + " justify-between"}>
                    <span>{placeholder || (options[0] ? "Select…" : "Select an option")}</span>
                    <span className="text-[#C0C0C0]">▾</span>
                </div>
            );
        case "multi_select":
            return (
                <div className="space-y-1.5">
                    {(options.length ? options : ["Option 1", "Option 2"]).map((o, i) => (
                        <label key={i} className="flex items-center gap-2 text-[13px] text-[#6F6A64]">
                            <span className="w-4 h-4 rounded border border-[#CFC9C1] bg-white inline-block" /> {o || `Option ${i + 1}`}
                        </label>
                    ))}
                </div>
            );
        case "checkbox":
            return (
                <label className="flex items-center gap-2 text-[13px] text-[#6F6A64]">
                    <span className="w-4 h-4 rounded border border-[#CFC9C1] bg-white inline-block" /> {placeholder || "Yes / confirmed"}
                </label>
            );
        case "yes_no":
            return (
                <div className="flex items-center gap-2">
                    {["Yes", "No", "N/A"].map((v) => (
                        <span key={v} className="px-3.5 py-1.5 rounded-lg border border-[#E5E0DA] bg-white text-[13px] text-[#6F6A64]">{v}</span>
                    ))}
                </div>
            );
        case "photo":
            return (
                <div className="border-2 border-dashed border-[#E5E0DA] rounded-lg p-5 flex flex-col items-center justify-center text-[#9A968F] bg-[#FAF9F7]">
                    <Camera size={20} className="mb-1.5" />
                    <span className="text-[12px]">Tap to add a photo</span>
                </div>
            );
        case "signature":
            return <SignatureBox />;
        case "check_grid": {
            const g = field.grid || {};
            const idCols = g.id_columns || [], chkCols = g.check_columns || [], rows = g.rows || [];
            return (
                <div className="border border-[#E5E0DA] rounded-lg p-3 bg-[#FAF9F7] text-[12px] text-[#6F6A64]">
                    Table with {idCols.length} reference column{idCols.length !== 1 ? "s" : ""}, {chkCols.length} check column{chkCols.length !== 1 ? "s" : ""} and {rows.length} row{rows.length !== 1 ? "s" : ""}
                    {idCols.length > 0 || chkCols.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {[...idCols, ...chkCols].slice(0, 8).map((c) => (
                                <span key={c.id} className="px-2 py-0.5 rounded-full bg-white border border-[#E5E0DA] text-[11px]">{c.label || "—"}</span>
                            ))}
                        </div>
                    ) : null}
                </div>
            );
        }
        case "text":
        default:
            return <div className={fakeInput}>{placeholder || "Short answer text…"}</div>;
    }
}

function SignatureBox() {
    return (
        <div className="border border-[#E5E0DA] rounded-lg h-20 bg-[#FAF9F7] flex items-center justify-center text-[#C7C2BB]">
            <PenLine size={16} className="mr-1.5" /> <span className="text-[12px]">Sign here</span>
        </div>
    );
}
